import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookOpen, ExternalLink, Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { KnowledgeArticleView, KnowledgeBrowser } from '../../components/knowledge';
import { useToast } from '../../components/toast';
import { Button, ConfirmDialog, EmptyState, ErrorState, PageHeader, PageLoader } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { knowledgeImageUrl, type KnowledgeArticle, type KnowledgePage, type KnowledgeTagCounts } from '../../lib/knowledge';

/** The knowledgebase inside the app: articles for whoever's signed in, and for technicians, where they write them. */
export function KnowledgeListPage() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const staff = can('VIEWER');
  const canWrite = can('TECHNICIAN');
  const newArticle = (
    <Button variant="primary" icon={<Plus />} onClick={() => navigate('/knowledge/new')}>
      New article
    </Button>
  );

  return (
    <>
      <PageHeader
        title="Knowledgebase"
        description={
          staff
            ? 'How-tos and answers for the public, students, and staff, and notes for the technology team.'
            : 'How-tos and answers from the technology team.'
        }
        actions={canWrite && newArticle}
      />
      <KnowledgeBrowser
        queryKey={['knowledge']}
        fetchList={(filters) => api.get<KnowledgePage>('/knowledge', filters)}
        fetchTags={() => api.get<KnowledgeTagCounts>('/knowledge/tags')}
        articleHref={(id) => `/knowledge/${id}`}
        staff={staff}
        empty={
          <EmptyState
            icon={<BookOpen />}
            title="No articles yet"
            description={
              canWrite
                ? 'Write how-tos and answers for the public, students, staff, or just your team, with screenshots and tags for devices, software, and topics.'
                : 'Articles from the technology team will show up here.'
            }
            action={canWrite && newArticle}
          />
        }
      />
    </>
  );
}

export function KnowledgeArticlePage() {
  const { id } = useParams();
  const { can } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [deleting, setDeleting] = useState(false);
  const article = useQuery({ queryKey: ['knowledge', 'article', id], queryFn: () => api.get<KnowledgeArticle>(`/knowledge/${id}`) });

  const remove = useMutation({
    mutationFn: () => api.delete(`/knowledge/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['knowledge'] });
      toast.success('Article deleted');
      navigate('/knowledge');
    },
    onError: (err) => {
      setDeleting(false);
      toast.error(errorMessage(err));
    },
  });

  if (article.isLoading) return <PageLoader />;
  if (article.error || !article.data) return <ErrorState error={article.error} onRetry={() => article.refetch()} />;
  const { permissions, visibility } = article.data;

  return (
    <>
      <KnowledgeArticleView
        article={article.data}
        imageSrc={knowledgeImageUrl}
        listPath="/knowledge"
        staff={can('VIEWER')}
        actions={
          <>
            {visibility.includes('PUBLIC') && (
              <Link
                to={`/help/${article.data.id}`}
                target="_blank"
                className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <ExternalLink className="size-4" /> Public page
              </Link>
            )}
            {permissions?.delete && (
              <Button variant="ghost" icon={<Trash2 />} onClick={() => setDeleting(true)}>
                Delete
              </Button>
            )}
            {permissions?.edit && (
              <Button variant="primary" icon={<Pencil />} onClick={() => navigate(`/knowledge/${article.data!.id}/edit`)}>
                Edit
              </Button>
            )}
          </>
        }
      />
      <ConfirmDialog
        open={deleting}
        onClose={() => setDeleting(false)}
        onConfirm={() => remove.mutate()}
        loading={remove.isPending}
        title={`Delete “${article.data.title}”?`}
        description="The article and its pictures are removed for everyone. This can’t be undone."
        confirmLabel="Delete article"
      />
    </>
  );
}
